/**
 * The IPO subscription calendar's dates, and which of them are marked
 * tentative.
 *
 * Separate from the form so the payload rule can be tested without rendering
 * the screen, which drags in a PDF builder.
 *
 * The service takes one `[IpoDateField!]` list rather than seven booleans, so
 * an eighth date later costs one enum value instead of seven fields across
 * three input blocks.
 */

/** `[form field, label, IpoDateField enum value]`, in calendar order. */
export const DATE_FIELDS = [
  ['offerAnnouncementDate', 'Offer announcement', 'OFFER_ANNOUNCEMENT'],
  ['subscriptionStartDate', 'Subscription opens', 'SUBSCRIPTION_START'],
  ['subscriptionEndDate', 'Subscription closes', 'SUBSCRIPTION_END'],
  [
    'allocationAnnouncementDate',
    'Allocation announced',
    'ALLOCATION_ANNOUNCEMENT',
  ],
  ['refundDate', 'Refunds', 'REFUND'],
  ['expectedListingDate', 'Expected listing', 'EXPECTED_LISTING'],
  ['sellUnlockDate', 'Sell unlock', 'SELL_UNLOCK'],
];

/** The form field holding one date's tentative switch. */
export const tentativeField = enumValue => `tentative_${enumValue}`;

/**
 * The app folds Allocation announced and Refunds into one timeline row, so
 * flagging either makes that whole row read tentative.
 */
export const SHARED_ROW = ['ALLOCATION_ANNOUNCEMENT', 'REFUND'];

/**
 * The complete set of flagged dates, as the enum values the service expects.
 *
 * Always the whole list: `tentativeDates` replaces what is stored rather than
 * merging into it, so posting one value would clear every other flag. An
 * empty array is meaningful — it is how the last flag is removed. Built by
 * walking DATE_FIELDS, so the result is unique and in calendar order, and the
 * service's "no repeated values" rule cannot be broken by construction.
 */
export const collectTentativeDates = (values = {}) =>
  DATE_FIELDS.filter(
    ([, , enumValue]) => values[tentativeField(enumValue)],
  ).map(([, , enumValue]) => enumValue);

/** The stored list as one boolean per date, for the form. */
export const tentativeDatesToForm = (stored = []) =>
  Object.fromEntries(
    DATE_FIELDS.map(([, , enumValue]) => [
      tentativeField(enumValue),
      (stored ?? []).includes(enumValue),
    ]),
  );
